import { type ElementStyles, css, Observable, type Subscriber } from '@microsoft/fast-element';
import type { HoverIconDefinition, HoverIcon } from './HoverIcon';
import { resetFocus } from 'owa-mail-focus-manager';
import { lazyToggleRowReadState } from 'owa-mail-mark-read-actions';
import { markAsRead } from 'owa-locstrings/lib/strings/markasread.locstring.json';
import { markAsUnread } from 'owa-locstrings/lib/strings/markasunread.locstring.json';
import {
    MailFilled,
    MailReadFilled,
    MailReadRegular,
    MailRegular,
} from '@fluentui/react-icons/lib/fonts';

const MarkReadRegularFontGlyph = MailReadRegular.codepoint;
const MarkReadFilledFontGlyph = MailReadFilled.codepoint;
const MailUnreadRegularFontGlyph = MailRegular.codepoint;
const MailUnreadFilledFontGlyph = MailFilled.codepoint;

const BaseReadUnreadIconStyles: ElementStyles = css`
    :host {
        color: var(--colorNeutralForeground3);
    }

    :host(:hover),
    :host(:active) {
        color: var(--colorNeutralForeground3Hover);
    }
`;

const MarkReadHoverIconStyles: ElementStyles = css`
    .icon:before {
        content: '${MarkReadRegularFontGlyph}';
    }

    :host(:hover) .icon:before,
    :host(:active) .icon:before {
        content: '${MarkReadFilledFontGlyph}';
    }
`;
const MarkUnreadHoverIconStyles: ElementStyles = css`
    .icon:before {
        content: '${MailUnreadRegularFontGlyph}';
    }

    :host(:hover) .icon:before,
    :host(:active) .icon:before {
        content: '${MailUnreadFilledFontGlyph}';
    }
`;

const subscribers = new WeakMap<Readonly<HoverIcon>, Subscriber>();

function getSubscriber(subject: Readonly<HoverIcon>): Subscriber {
    const subscriber = subscribers.get(subject) ?? {
        handleChange: applyStyles,
    };
    subscribers.set(subject, subscriber);
    return subscriber;
}

function applyStyles(subject: Readonly<HoverIcon>) {
    if (!subject.hasUnread) {
        subject.$fastController.addStyles(MarkUnreadHoverIconStyles);
        subject.$fastController.removeStyles(MarkReadHoverIconStyles);
    } else {
        subject.$fastController.addStyles(MarkReadHoverIconStyles);
        subject.$fastController.removeStyles(MarkUnreadHoverIconStyles);
    }
}

export const ReadUnread: HoverIconDefinition = {
    title(this: Readonly<HoverIcon>) {
        return this.hasUnread ? markAsRead : markAsUnread;
    },
    connectedCallback(this: Readonly<HoverIcon>) {
        applyStyles(this);
        Observable.getNotifier(this).subscribe(getSubscriber(this), 'hasUnread');
    },
    customCss: BaseReadUnreadIconStyles,
    disconnectedCallback(this: Readonly<HoverIcon>) {
        Observable.getNotifier(this).unsubscribe(getSubscriber(this), 'hasUnread');
    },
    onClick(this: HoverIcon) {
        lazyToggleRowReadState.importAndExecute(this.rowKey, this.tableViewId, 'Hover');
        // Reset focus after the stitch to mark the item as read is raised
        resetFocus('MailListToggleMarkAsRead');
    },
};
